import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import moment from 'moment';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { FiClock, FiCheck, FiX, FiCalendar, FiLogIn, FiLogOut, FiChevronLeft, FiChevronRight, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './AttendanceTracker.css';

// ── Night shift (9:30 PM – 5:30 AM) ───────────────────────
// A record's `date` is the day the shift STARTED, saved as midnight in the
// office timezone. Calendar columns are plain UTC dates (device-independent),
// so records are keyed by their office-timezone date to land in the right one.
const OFFICE_TZ = process.env.REACT_APP_OFFICE_TIMEZONE || 'Asia/Karachi';
const recordKey = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: OFFICE_TZ });

// Times before noon still belong to the previous day's shift
const SHIFT_CUTOFF_MS = 12 * 60 * 60 * 1000;
const currentShiftKey = () => recordKey(Date.now() - SHIFT_CUTOFF_MS);

// Weekly off days by shift start date (0 = Sunday … 6 = Saturday)
const OFF_DAYS = (process.env.REACT_APP_OFF_DAYS || '6,0').split(',').map(Number);
const isOffDay = (d) => OFF_DAYS.includes(moment.utc(d).day());

// ── Formatters ───────────────────────────────────────────
const fmt = (t) => t ? new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '--:--';

// Short pill-style time, e.g. "10am", "7:15pm" — matches the compact
// look of a schedule grid instead of the fuller "10:00 AM" format.
const fmtShort = (t) => {
  if (!t) return null;
  const d = new Date(t);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return m === 0 ? `${h}${ampm}` : `${h}:${String(m).padStart(2, '0')}${ampm}`;
};

// "1.5" -> "1h 30mins"
const fmtHoursMins = (hours = 0) => {
  const totalMinutes = Math.round((hours || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0 && m === 0) return '0h';
  if (m === 0) return `${h}h`;
  return `${h}h ${m}mins`;
};

// Live "2h 05m" counter for an open shift
const fmtElapsed = (from, now) => {
  const mins = Math.max(0, Math.floor((now - new Date(from)) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};

// Calendar column key (columns are UTC midnight dates)
const dateKey = (d) => moment.utc(d).format('YYYY-MM-DD');

const dayShort = (d) => moment.utc(d).format('ddd');
const dayNum = (d) => moment.utc(d).format('MMM D');

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

const TodaySkeleton = memo(function TodaySkeleton() {
  return (
    <div className="at-card at-skeleton-card" aria-hidden="true">
      <div className="at-today-header">
        <div className="at-today-left">
          <div className="at-skeleton at-sk-icon" />
          <div className="at-sk-text-group">
            <div className="at-skeleton at-sk-title" />
            <div className="at-skeleton at-sk-subtitle" />
          </div>
        </div>
      </div>
      <div className="at-actions">
        <div className="at-skeleton at-sk-btn" />
        <div className="at-skeleton at-sk-btn" />
      </div>
    </div>
  );
});
const WeekTableSkeleton = memo(function WeekTableSkeleton() {
  return (
    <div className="at-week-table-wrapper" aria-hidden="true">
      <div className="at-week-skeleton">
        <div className="at-week-skeleton-head">
          <div className="at-skeleton at-sk-head-corner" />
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="at-skeleton at-sk-head-day" />
          ))}
        </div>
        <div className="at-week-skeleton-row">
          <div className="at-skeleton at-sk-member" />
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="at-skeleton at-sk-cell" />
          ))}
        </div>
      </div>
    </div>
  );
});

// ── Memoized day-header cell ────────────────────────────────
// Re-renders only when its own date or computed total changes —
// not on every parent state change (action loading, etc).
const DayHeaderCell = memo(function DayHeaderCell({ date, totalLabel, isToday }) {
  return (
    <div className={`at-day-head ${isToday ? 'is-today' : ''}`}>
      <div className="at-day-head-name">{dayShort(date)}, {dayNum(date)}</div>
      <div className="at-day-head-total">{totalLabel}</div>
    </div>
  );
}, (prev, next) => (
  prev.totalLabel === next.totalLabel &&
  prev.isToday === next.isToday &&
  prev.date.getTime() === next.date.getTime()
));

// ── Shift pill ("9:30pm – 5:30am", with an AUTO tag when the system closed it)
const ShiftPill = ({ record }) => {
  const outT = fmtShort(record.checkOutTime);
  return (
    <div className="at-shift-pill" title={record.autoCheckedOut ? 'Auto checked-out at shift end' : undefined}>
      {fmtShort(record.checkInTime)}{outT ? ` – ${outT}` : ' – …'}
      {record.autoCheckedOut && <span className="at-auto-tag">auto</span>}
      {record.isLate && <span className="at-late-tag" title="Checked in after 9:45 PM">late</span>}
    </div>
  );
};

// ── Memoized day cell (the shift pill / status badge) ───────
const DayCell = memo(function DayCell({ record, off }) {
  if (!record) {
    return (
      <div className="at-day-cell">
        {off ? <span className="at-cell-badge off">Off</span> : <span className="at-cell-empty">—</span>}
      </div>
    );
  }

  if (record.status === 'absent') {
    return <div className="at-day-cell"><span className="at-cell-badge absent">Absent</span></div>;
  }
  if (record.status === 'on_leave') {
    return (
      <div className="at-day-cell">
        <span className="at-cell-badge leave">{record.leaveType ? `Leave (${record.leaveType})` : 'Leave'}</span>
      </div>
    );
  }

  if (!record.checkInTime) return <div className="at-day-cell"><span className="at-cell-empty">—</span></div>;

  return (
    <div className="at-day-cell">
      <ShiftPill record={record} />
      {record.workingHours > 0 && (
        <div className="at-cell-hours"><FiClock size={10} /> {fmtHoursMins(record.workingHours)}</div>
      )}
    </div>
  );
}, (prev, next) => (
  prev.off === next.off &&
  prev.record?._id === next.record?._id &&
  prev.record?.checkInTime === next.record?.checkInTime &&
  prev.record?.checkOutTime === next.record?.checkOutTime &&
  prev.record?.workingHours === next.record?.workingHours &&
  prev.record?.status === next.record?.status &&
  prev.record?.autoCheckedOut === next.record?.autoCheckedOut &&
  prev.record?.isLate === next.record?.isLate
));

// ── Mobile day row (stacked list instead of the wide table) ─
const DayListRow = memo(function DayListRow({ date, record, isToday }) {
  let right;
  if (!record || (!record.checkInTime && !['absent', 'on_leave'].includes(record.status))) {
    right = isOffDay(date)
      ? <span className="at-cell-badge off">Off</span>
      : <span className="at-cell-empty">—</span>;
  } else if (record.status === 'absent') {
    right = <span className="at-cell-badge absent">Absent</span>;
  } else if (record.status === 'on_leave') {
    right = <span className="at-cell-badge leave">{record.leaveType ? `Leave (${record.leaveType})` : 'Leave'}</span>;
  } else {
    right = (
      <div className="at-list-right">
        <ShiftPill record={record} />
        {record.workingHours > 0 && <div className="at-cell-hours">{fmtHoursMins(record.workingHours)}</div>}
      </div>
    );
  }
  return (
    <div className={`at-list-row ${isToday ? 'is-today' : ''}`}>
      <div className="at-list-date">
        <span className="at-list-day">{dayShort(date)}</span>
        <span className="at-list-num">{dayNum(date)}</span>
      </div>
      {right}
    </div>
  );
});

// ── Memoized Today card ──────────────────────────────────────
// Split out so it never re-renders when only week/history state
// changes (and vice versa for WeekCard below).
const TodayCard = memo(function TodayCard({
  attendance, today, shiftDateLabel, checkedIn, checkedOut, isOnLeave, actionLoading, onCheckIn, onCheckOut,
}) {
  // Tick every 30s while a shift is open so the elapsed counter stays live
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!checkedIn || checkedOut) return undefined;
    setNow(Date.now());
    const iv = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(iv);
  }, [checkedIn, checkedOut]);

  return (
    <div className="at-card">
      <div className="at-today-header">
        <div className="at-today-left">
          <div className="at-today-icon" style={{ background: today.bg, color: 'white' }}>
            {today.icon}
          </div>
          <div>
            <div className="at-today-label" style={{ color: today.color }}>{today.label}</div>
            <div className="at-today-date">
              <FiCalendar size={12} />
              {shiftDateLabel}
              <span className="at-dot">•</span>
              Shift 9:30 PM – 5:30 AM
            </div>
          </div>
        </div>
        {checkedIn && (
          <div className="at-times">
            <div className="at-time-pill in">
              <FiLogIn /> {fmt(attendance.checkInTime)}
              {attendance.isLate && <span className="at-late-tag" title="Checked in after 9:45 PM">late {attendance.lateMinutes}m</span>}
            </div>
            {checkedOut && (
              <div
                className="at-time-pill out"
                title={attendance.autoCheckedOut ? 'No check-out by 6:30 AM, so it was set to shift end automatically' : undefined}
              >
                <FiLogOut /> {fmt(attendance.checkOutTime)}
                {attendance.autoCheckedOut && <span className="at-auto-tag">auto</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {checkedIn && (
        <div className={`at-hours-bar ${checkedOut ? '' : 'live'}`}>
          <FiClock />
          {checkedOut ? (
            <span>Worked <strong>{fmtHoursMins(attendance.workingHours)}</strong> this shift</span>
          ) : (
            <span><span className="at-live-dot" />On shift for <strong>{fmtElapsed(attendance.checkInTime, now)}</strong></span>
          )}
        </div>
      )}

      {!isOnLeave && (
        <div className="at-actions">
          <button className="at-btn at-btn-in" onClick={onCheckIn} disabled={checkedIn || actionLoading !== null}>
            {actionLoading === 'in' ? <span className="at-spinner" /> : <FiLogIn />}
            {checkedIn ? 'Checked In' : 'Check In'}
          </button>
          <button className="at-btn at-btn-out" onClick={onCheckOut} disabled={!checkedIn || checkedOut || actionLoading !== null}>
            {actionLoading === 'out' ? <span className="at-spinner" /> : <FiLogOut />}
            {checkedOut ? 'Checked Out' : 'Check Out'}
          </button>
        </div>
      )}
    </div>
  );
});

// ── Memoized Week card ────────────────────────────────────────
// `isRefreshing` only ever shows a small inline spinner next to the
// week label — the table itself stays mounted with its last known
// data, so there is no skeleton flash on background refetches.
const WeekCard = memo(function WeekCard({
  weekLabel, isCurrentWeek, onPrevWeek, onNextWeek, showSkeleton, isRefreshing,
  weekDays, todayKey, historyByDay, user, weekTotalHours, weekShifts, isEmpty,
}) {
  const displayName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'You';

  return (
    <div className="at-card at-week-card">
      <div className="at-week-header">
        <button className="at-week-nav-btn" onClick={onPrevWeek} aria-label="Previous week"><FiChevronLeft /></button>
        <div className="at-week-title">
          <span className="at-week-label">
            {weekLabel}
            {isRefreshing && <FiRefreshCw className="at-refresh-spin" size={13} aria-label="Refreshing" />}
          </span>
          <span className="at-week-sub">
            {fmtHoursMins(weekTotalHours)} · {weekShifts} {weekShifts === 1 ? 'shift' : 'shifts'}
          </span>
        </div>
        <button className="at-week-nav-btn" onClick={onNextWeek} disabled={isCurrentWeek} aria-label="Next week"><FiChevronRight /></button>
      </div>

      {showSkeleton ? (
        <WeekTableSkeleton />
      ) : (
        <>
          <div className="at-week-table-wrapper">
            <div className="at-week-table">
              {/* Header row */}
              <div className="at-week-row at-week-row-head">
                <div className="at-week-corner">Team member</div>
                {weekDays.map((d) => {
                  const rec = historyByDay.get(dateKey(d));
                  return (
                    <DayHeaderCell
                      key={dateKey(d)}
                      date={d}
                      isToday={dateKey(d) === todayKey}
                      totalLabel={fmtHoursMins(rec?.workingHours || 0)}
                    />
                  );
                })}
              </div>

              {/* Single member row (current user) */}
              <div className="at-week-row">
                <div className="at-week-member">
                  {user?.profilePicture ? (
                    <img className="at-avatar" src={user.profilePicture} alt="" />
                  ) : (
                    <div className="at-avatar">{initials(displayName)}</div>
                  )}
                  <div>
                    <div className="at-member-name">{displayName}</div>
                    <div className="at-member-total">{fmtHoursMins(weekTotalHours)}</div>
                  </div>
                </div>
                {weekDays.map((d) => (
                  <DayCell key={dateKey(d)} record={historyByDay.get(dateKey(d))} off={isOffDay(d)} />
                ))}
              </div>
            </div>
          </div>

          {/* Phones: one row per day instead of the wide table */}
          <div className="at-week-list">
            {weekDays.map((d) => (
              <DayListRow
                key={dateKey(d)}
                date={d}
                isToday={dateKey(d) === todayKey}
                record={historyByDay.get(dateKey(d))}
              />
            ))}
          </div>
        </>
      )}

      {!showSkeleton && isEmpty && (
        <div className="at-empty">No records for this week</div>
      )}
    </div>
  );
});

// ── Helpers for week math ───────────────────────────────────
// UTC-based so week boundaries never shift with the device's local
// timezone/clock (this is what was causing the phone/laptop mismatch).
const startOfWeek = (d) => moment.utc(d).startOf('day').day(0).toDate();

// `onViewTeam` (optional) shows a button that switches to the team attendance dashboard
const AttendanceTracker = ({ onViewTeam }) => {
  const { user } = useAuthStore();
  const [attendance, setAttendance] = useState(null);
  const [workDate, setWorkDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [history, setHistory] = useState([]);
  // Start on the week of the CURRENT SHIFT (at 2 AM Sunday that's still Saturday's shift)
  const [weekStart, setWeekStart] = useState(() => startOfWeek(currentShiftKey()));
  const [historyLoading, setHistoryLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Tracks whether *any* week has ever finished loading. Once true,
  // later fetches (polling, check-in/out, switching weeks) never show
  // the big skeleton again — only a small "refreshing" indicator.
  const firstLoadDoneRef = useRef(false);
  // In-flight request guard so a fast week-flip doesn't let an older
  // response overwrite a newer one.
  const abortRef = useRef(null);
  // Per-week cache so revisiting a week you've already seen paints
  // instantly while it silently revalidates in the background.
  const historyCacheRef = useRef(new Map());

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/attendance/my-status');
      setAttendance(res.data.attendance);
      if (res.data.date) setWorkDate(res.data.date);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (weekStartDate) => {
    const key = dateKey(weekStartDate);
    const cached = historyCacheRef.current.get(key);

    // Cancel any older in-flight request before starting a new one.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (cached) {
      // Instant paint from cache, then revalidate quietly.
      setHistory(cached);
      setHistoryLoading(false);
      setIsRefreshing(true);
    } else if (!firstLoadDoneRef.current) {
      // Genuinely the very first load: ok to show the full skeleton.
      setHistoryLoading(true);
    } else {
      // A later, never-visited week: keep whatever is on screen and
      // just flag that a refresh is happening (no skeleton flash).
      setIsRefreshing(true);
    }

    try {
      // Pad by a day each side: shift dates are office-time midnights,
      // which sit on the previous UTC day
      const start = moment.utc(weekStartDate).subtract(1, 'day').toDate();
      const end = moment.utc(weekStartDate).add(7, 'days').toDate();
      const res = await api.get(
        `/attendance/history?startDate=${start.toISOString()}&endDate=${end.toISOString()}&limit=100`,
        { signal: controller.signal }
      );
      const records = res.data.attendances || [];
      historyCacheRef.current.set(key, records);
      setHistory(records);
    } catch (e) {
      const aborted = e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || e?.name === 'AbortError';
      if (!aborted) {
        console.error(e);
        toast.error('Failed to load attendance history');
      }
      return; // don't flip loading flags for a stale/aborted request
    }
    setHistoryLoading(false);
    setIsRefreshing(false);
    firstLoadDoneRef.current = true;
  }, []);

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  useEffect(() => {
    fetchHistory(weekStart);
    return () => abortRef.current?.abort();
  }, [weekStart, fetchHistory]);

  // Invalidate the current week's cache after an action changes it,
  // so the next fetch doesn't serve stale cached data as if fresh.
  const refreshCurrentWeek = useCallback(() => {
    historyCacheRef.current.delete(dateKey(weekStart));
    return fetchHistory(weekStart);
  }, [fetchHistory, weekStart]);

  const handleCheckIn = useCallback(async () => {
    setActionLoading('in');
    try {
      await api.post('/attendance/check-in');
      toast.success('Checked in!');
      await fetchStatus();
      await refreshCurrentWeek();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to check in');
    } finally { setActionLoading(null); }
  }, [fetchStatus, refreshCurrentWeek]);

  const handleCheckOut = useCallback(async () => {
    setActionLoading('out');
    try {
      await api.post('/attendance/check-out');
      toast.success('Checked out!');
      await fetchStatus();
      await refreshCurrentWeek();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to check out');
    } finally { setActionLoading(null); }
  }, [fetchStatus, refreshCurrentWeek]);

  const checkedIn = !!attendance?.checkInTime;
  const checkedOut = !!attendance?.checkOutTime;
  const isOnLeave = attendance?.status === 'on_leave';
  const isOff = attendance?.status === 'off' && !checkedIn;

  const today = useMemo(() => {
    if (!attendance) return { label: 'Not Checked In', color: '#ef4444', bg: '#fef2f2', icon: <FiX /> };
    if (isOnLeave) return { label: 'On Leave', color: '#f59e0b', bg: '#fffbeb', icon: <FiCalendar /> };
    if (isOff) return { label: 'Weekend Off', color: '#6b7280', bg: '#9ca3af', icon: <FiCalendar /> };
    if (checkedOut) return { label: 'Shift Complete', color: '#3E2987', bg: '#B9B1FF', icon: <FiCheck /> };
    if (checkedIn) return { label: 'On Shift', color: '#3E2987', bg: '#B9B1FF', icon: <FiClock /> };
    return { label: 'Not Checked In', color: '#ef4444', bg: '#fef2f2', icon: <FiX /> };
  }, [attendance, isOnLeave, isOff, checkedIn, checkedOut]);

  // The shift's date (Oct 15 even at 3 AM on Oct 16)
  const todayKey = useMemo(
    () => (workDate ? recordKey(workDate) : currentShiftKey()),
    [workDate]
  );
  const shiftDateLabel = useMemo(
    () => moment.utc(todayKey).format('dddd, MMMM D'),
    [todayKey]
  );

  // 7 Date objects (Sun -> Sat) for the selected week — recomputed
  // only when weekStart actually changes. UTC-based to match dateKey.
  const weekDays = useMemo(() => (
    Array.from({ length: 7 }, (_, i) => moment.utc(weekStart).add(i, 'days').toDate())
  ), [weekStart]);

  // Map each shift date to its attendance record for O(1) lookup per cell.
  const historyByDay = useMemo(() => {
    const map = new Map();
    history.forEach((r) => map.set(recordKey(r.date), r));
    return map;
  }, [history]);

  // Only this week's records (the fetch is padded by a day each side)
  const weekRecords = useMemo(
    () => weekDays.map((d) => historyByDay.get(dateKey(d))).filter(Boolean),
    [weekDays, historyByDay]
  );

  // e.g. "Oct 11 – 17, 2026" or "Sep 27 – Oct 3, 2026"
  const weekLabel = useMemo(() => {
    const start = moment.utc(weekStart);
    const end = moment.utc(weekDays[6]);
    const endStr = start.month() === end.month() ? end.format('D') : end.format('MMM D');
    return `${start.format('MMM D')} – ${endStr}, ${end.format('YYYY')}`;
  }, [weekStart, weekDays]);

  const isCurrentWeek = useMemo(() => (
    startOfWeek(todayKey).getTime() === weekStart.getTime()
  ), [weekStart, todayKey]);

  const prevWeek = useCallback(() => setWeekStart((d) => moment.utc(d).subtract(7, 'days').toDate()), []);
  const nextWeek = useCallback(() => setWeekStart((d) => moment.utc(d).add(7, 'days').toDate()), []);

  const weekTotalHours = useMemo(
    () => weekRecords.reduce((s, r) => s + (r.workingHours || 0), 0),
    [weekRecords]
  );
  const weekShifts = useMemo(
    () => weekRecords.filter((r) => r.checkInTime).length,
    [weekRecords]
  );

  if (loading) return <div className="at-page"><TodaySkeleton /></div>;

  return (
    <div className="at-page">
      {onViewTeam && (
        <div className="at-team-bar">
          <button className="at-team-btn" onClick={onViewTeam}>
            <FiUsers /> View Team Attendance
          </button>
        </div>
      )}

      <TodayCard
        attendance={attendance}
        today={today}
        shiftDateLabel={shiftDateLabel}
        checkedIn={checkedIn}
        checkedOut={checkedOut}
        isOnLeave={isOnLeave}
        actionLoading={actionLoading}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
      />

      <WeekCard
        weekLabel={weekLabel}
        isCurrentWeek={isCurrentWeek}
        onPrevWeek={prevWeek}
        onNextWeek={nextWeek}
        showSkeleton={historyLoading}
        isRefreshing={isRefreshing}
        weekDays={weekDays}
        todayKey={todayKey}
        historyByDay={historyByDay}
        user={user}
        weekTotalHours={weekTotalHours}
        weekShifts={weekShifts}
        isEmpty={weekRecords.length === 0}
      />
    </div>
  );
};

export default memo(AttendanceTracker);
